import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChat } from '../context/ChatContext';
import { useTier } from '../context/TierContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { UpgradePrompt } from '../components/FeatureGate';
import {
  MessageSquare,
  Send,
  ArrowLeft,
  Plus,
  Users,
  Briefcase,
  Loader2,
  UserPlus,
  ExternalLink
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const ChatPage = () => {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const { user, api } = useAuth();
  const { hasFeature } = useTier();
  const canUseChat = hasFeature('messaging');
  const {
    rooms,
    activeRoom,
    messages,
    loading,
    openRoom,
    closeChat,
    sendMessage,
    createRoom,
    fetchRooms
  } = useChat();

  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showNewRoom, setShowNewRoom] = useState(false);
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [operators, setOperators] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [addingParticipant, setAddingParticipant] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Open room if roomId provided in URL
  useEffect(() => {
    if (roomId && rooms.length > 0) {
      const room = rooms.find(r => r.id === roomId);
      if (room && (!activeRoom || activeRoom.id !== roomId)) {
        openRoom(room);
      }
    }
  }, [roomId, rooms, activeRoom, openRoom]);

  // Fetch operators when dialog opens
  useEffect(() => {
    if ((showNewRoom || showAddParticipant) && api) {
      setLoadingOperators(true);
      api.get('/operators')
        .then(res => setOperators(res.data))
        .catch(err => console.error('Failed to fetch operators:', err))
        .finally(() => setLoadingOperators(false));
    }
  }, [showNewRoom, showAddParticipant, api]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!messageInput.trim() || sending) return;

    setSending(true);
    try {
      await sendMessage(messageInput.trim());
      setMessageInput('');
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return;

    try {
      const room = await createRoom(newRoomName.trim(), 'general', null, selectedOperators);
      setShowNewRoom(false);
      setNewRoomName('');
      setSelectedOperators([]);
      if (room) {
        navigate(`/chat/${room.id}`);
      }
    } catch (err) {
      console.error('Failed to create room:', err);
    }
  };

  const toggleOperator = (operatorId) => {
    setSelectedOperators(prev => 
      prev.includes(operatorId)
        ? prev.filter(id => id !== operatorId)
        : [...prev, operatorId]
    );
  };

  const handleAddParticipants = async () => {
    if (!activeRoom || selectedOperators.length === 0) return;
    
    setAddingParticipant(true);
    try {
      for (const operatorId of selectedOperators) {
        await api.post(`/chat/rooms/${activeRoom.id}/participants?operator_id=${operatorId}`);
      }
      // Refresh rooms to get updated participant list
      await fetchRooms();
      // Re-open the room to refresh participant count
      const updatedRoom = rooms.find(r => r.id === activeRoom.id);
      if (updatedRoom) {
        openRoom(updatedRoom);
      }
      setShowAddParticipant(false);
      setSelectedOperators([]);
    } catch (err) {
      console.error('Failed to add participants:', err);
    } finally {
      setAddingParticipant(false);
    }
  };

  // Get operators not already in the active room
  const availableOperators = operators.filter(op => 
    !activeRoom?.participants?.some(p => p.id === op.id)
  );

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return format(date, 'HH:mm');
    } else if (diffDays === 1) {
      return 'Yesterday ' + format(date, 'HH:mm');
    } else if (diffDays < 7) {
      return format(date, 'EEE HH:mm');
    }
    return format(date, 'dd/MM HH:mm');
  };

  const handleSelectRoom = (room) => {
    navigate(`/chat/${room.id}`);
  };

  const handleBackToList = () => {
    closeChat();
    navigate('/chat');
  };

  // Upgrade prompt for locked feature
  if (!canUseChat) {
    return (
      <div className="h-full flex items-center justify-center">
        <UpgradePrompt
          feature="messaging"
          title="Chat & Messaging"
          description="Real-time chat between office and job site is available on Advanced and Professional plans."
        />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-7rem)] flex">
      {/* Sidebar - Room List */}
      <div className={`w-80 border-r bg-muted/30 flex flex-col ${activeRoom ? 'hidden md:flex' : 'flex'}`}>
        <div className="h-14 border-b flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-[#ffd108]" />
            <h2 className="font-semibold">Messages</h2>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setShowNewRoom(true)}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        
        <ScrollArea className="flex-1">
          {rooms.length === 0 ? (
            <div className="text-center text-muted-foreground py-12 px-4">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No conversations yet</p>
              <p className="text-sm mt-1">Create a chat room to start messaging</p>
              <Button className="mt-4" onClick={() => setShowNewRoom(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Chat Room
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {rooms.map((room) => {
                // Parse room name into title and site
                const nameParts = room.name?.split(' - ') || [];
                let title, site;
                if (nameParts.length >= 3) {
                  title = `${nameParts[0]} - ${nameParts[1]}`;
                  site = nameParts.slice(2).join(' - ');
                } else if (nameParts.length === 2) {
                  title = nameParts[0];
                  site = nameParts[1];
                } else {
                  title = room.name;
                  site = null;
                }
                const hasUnread = room.unread_count > 0;
                
                return (
                  <button
                    key={room.id}
                    onClick={() => handleSelectRoom(room)}
                    className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                      activeRoom?.id === room.id ? 'bg-muted' : ''
                    } ${hasUnread ? 'bg-muted/30' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <div className={`p-2 rounded-full ${
                          room.room_type === 'job' ? 'bg-blue-500/10' : 'bg-[#ffd108]/20'
                        }`}>
                          {room.room_type === 'job' ? (
                            <Briefcase className="h-5 w-5 text-blue-500" />
                          ) : (
                            <Users className="h-5 w-5 text-[#ffd108]" />
                          )}
                        </div>
                        {hasUnread && (
                          <span className="absolute -top-1 -right-1 bg-[#ffd108] text-black text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                            {room.unread_count > 9 ? '9+' : room.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className={`font-medium truncate ${hasUnread ? 'font-semibold' : ''}`}>{title}</h4>
                          {room.last_message_at && (
                            <span className="text-xs text-muted-foreground ml-2 shrink-0">
                              {formatDistanceToNow(new Date(room.last_message_at), { addSuffix: true })}
                            </span>
                          )}
                        </div>
                        {site && (
                          <p className="text-xs text-muted-foreground truncate">{site}</p>
                        )}
                        {room.last_message && (
                          <p className="text-sm text-muted-foreground truncate mt-0.5">
                            {room.last_message}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {room.room_type === 'job' ? 'Job Chat' : 'General'}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {room.participants?.length || 0} members
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Main Chat Area */}
      <div className={`flex-1 flex flex-col ${!activeRoom ? 'hidden md:flex' : 'flex'}`}>
        {activeRoom ? (
          <>
            {/* Chat Header */}
            <div className="h-14 border-b flex items-center justify-between px-4 bg-muted/30">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBackToList}
                  className="p-2 hover:bg-muted rounded-lg transition-colors md:hidden"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className={`p-2 rounded-full ${
                  activeRoom.room_type === 'job' ? 'bg-blue-500/10' : 'bg-[#ffd108]/20'
                }`}>
                  {activeRoom.room_type === 'job' ? (
                    <Briefcase className="h-5 w-5 text-blue-500" />
                  ) : (
                    <Users className="h-5 w-5 text-[#ffd108]" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold">{activeRoom.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {activeRoom.participants?.length || 0} participants
                  </p>
                </div>
              </div>
              {/* Add Participant Button - Admin/Dispatcher only */}
              {(user?.role === 'admin' || user?.role === 'dispatcher') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddParticipant(true)}
                  className="gap-1"
                >
                  <UserPlus className="h-4 w-4" />
                  <span className="hidden sm:inline">Add</span>
                </Button>
              )}
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">
                  <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                <div className="space-y-3 max-w-3xl mx-auto">
                  {messages.map((msg, idx) => {
                    const isOwn = msg.sender_id === user?.id;
                    const isSystem = msg.type === 'system';

                    if (isSystem) {
                      return (
                        <div key={msg.id || idx} className="text-center text-xs text-muted-foreground py-1">
                          {msg.content}
                        </div>
                      );
                    }

                    return (
                      <div
                        key={msg.id || idx}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-4 py-2 ${
                            isOwn
                              ? 'bg-[#ffd108] text-black'
                              : 'bg-muted'
                          }`}
                        >
                          {!isOwn && (
                            <p className="text-xs font-medium mb-1 opacity-70">
                              {msg.sender_name}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {msg.content}
                          </p>
                          <p className={`text-[10px] mt-1 ${isOwn ? 'text-black/60' : 'text-muted-foreground'}`}>
                            {formatMessageTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>

            {/* Message Input */}
            <div className="p-4 border-t bg-background">
              <div className="flex gap-2 max-w-3xl mx-auto">
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  disabled={sending}
                  className="flex-1"
                />
                <Button
                  onClick={handleSend}
                  disabled={!messageInput.trim() || sending}
                  className="bg-[#ffd108] hover:bg-[#e5bc07] text-black"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Choose a chat room from the sidebar to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Create Room Dialog */}
      <Dialog open={showNewRoom} onOpenChange={(open) => {
        setShowNewRoom(open);
        if (!open) {
          setNewRoomName('');
          setSelectedOperators([]);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Chat Room</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="room-name">Room Name</Label>
              <Input
                id="room-name"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
                placeholder="e.g., Team Chat, Site A Discussion"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Add Participants (Operators)
              </Label>
              {loadingOperators ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : operators.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No operators available</p>
              ) : (
                <ScrollArea className="h-40 border rounded-md p-2">
                  <div className="space-y-2">
                    {operators.map((op) => (
                      <div
                        key={op.id}
                        className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                        onClick={() => toggleOperator(op.id)}
                      >
                        <Checkbox
                          id={`op-${op.id}`}
                          checked={selectedOperators.includes(op.id)}
                          onCheckedChange={() => toggleOperator(op.id)}
                        />
                        <label
                          htmlFor={`op-${op.id}`}
                          className="text-sm font-medium leading-none cursor-pointer flex-1"
                        >
                          {op.name}
                        </label>
                        {op.role && (
                          <Badge variant="secondary" className="text-xs">
                            {op.role}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {selectedOperators.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedOperators.length} operator(s) selected
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRoom(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateRoom} disabled={!newRoomName.trim()}>
              Create Room
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog open={showAddParticipant} onOpenChange={(open) => {
        setShowAddParticipant(open);
        if (!open) {
          setSelectedOperators([]);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Participants</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Current Participants */}
            {activeRoom?.participants?.length > 0 && (
              <div className="space-y-2">
                <Label className="text-muted-foreground">Current Participants</Label>
                <div className="flex flex-wrap gap-2">
                  {activeRoom.participants.map((p) => (
                    <Badge key={p.id} variant="secondary">
                      {p.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Add Operators to Chat
              </Label>
              {loadingOperators ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : availableOperators.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  All operators are already in this chat
                </p>
              ) : (
                <ScrollArea className="h-40 border rounded-md p-2">
                  <div className="space-y-2">
                    {availableOperators.map((op) => (
                      <div
                        key={op.id}
                        className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer"
                        onClick={() => toggleOperator(op.id)}
                      >
                        <Checkbox
                          id={`add-op-${op.id}`}
                          checked={selectedOperators.includes(op.id)}
                          onCheckedChange={() => toggleOperator(op.id)}
                        />
                        <label
                          htmlFor={`add-op-${op.id}`}
                          className="text-sm font-medium leading-none cursor-pointer flex-1"
                        >
                          {op.name}
                        </label>
                        {op.operator_code && (
                          <Badge variant="outline" className="text-xs">
                            {op.operator_code}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
              {selectedOperators.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedOperators.length} operator(s) selected
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddParticipant(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddParticipants} 
              disabled={selectedOperators.length === 0 || addingParticipant}
            >
              {addingParticipant ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add to Chat
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatPage;
